import React, { Component } from "react";
import {
    addMilliseconds,
    addMinutes,
    startOfMinute,
    subMilliseconds,
} from "date-fns";
import { filter as lodashFilter } from "lodash";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { logger } from "@formant/core/common/logger";
import { duration } from "@formant/core/model/duration";
import { INumericViewConfiguration } from "@formant/core/model/INumericViewConfiguration";
import { IStreamAggregateData } from "@formant/core/model/IStreamAggregateData";
import { IStreamData } from "@formant/core/model/IStreamData";

import { IAggregatesSpan } from "../../../store/IDeviceConfigurationStore";
import {
    deviceConfigurationStore,
    eventTriggerStore,
    observeStore,
    organizationStore,
    queryStore,
} from "../../../stores";
import { Lazy } from "../../Lazy";
import useModuleMemory from "../hooks/UseModuleMemory";
import { IBaseModuleProps } from "../IBaseModuleProps";
import { Module } from "../Module";
import { findConfiguration } from "../utils/findConfiguration";
import { multiValuedTagKeys } from "../utils/multiValuedTagKeys";
import { FilterSelector } from "./FilterSelector";
import * as styles from "./index.scss";
import SimpleNumericModule from "./SimpleNumericModule";
import { SpanSelector } from "./SpanSelector";
import { SplitBySelector } from "./SplitBySelector";

const Graph = Lazy(async () => (await import("../Graph")).Graph);

@observer
export class NumericModule extends Component<IBaseModuleProps> {
    @observable
    private span: number = 1 * duration.minute;
    @observable
    private splitByKey: string = "";
    @observable
    private selectedStreams: string[] = [];
    @observable
    private useModuleMemory = useModuleMemory();

    private data: IStreamData<"numeric">[] = [];
    private aggregateData: IStreamAggregateData<"numeric">[] = [];

    public componentDidMount() {
        const { streamName, configuration } = this.props;
        const { span, splitByKey } =
            findConfiguration(configuration, streamName, "numeric") || {};
        // check if span value exists in local storage
        const serializedState = this.useModuleMemory.rehydrate(streamName);

        if (serializedState) {
            const { span: localSpan } = serializedState;
            this.span = localSpan;
        } else if (span) {
            this.span = span;
        }
        if (typeof this.span !== "number") {
            logger.warn("NumericModule: span is not a number", {
                span: this.span,
            });
            this.span = 1 * duration.minute;
        }

        this.splitByKey = splitByKey || "";
    }

    public render() {
        const {
            filter,
            streamName,
            activeEvent,
            onSelectTime,
            configurationDisabled,
            device,
            layoutModule,
        } = this.props;
        const showOnlyCurrentValues =
            layoutModule?.configuration?.showOnlyCurrentValues;
        const { time, mode } = observeStore;
        const span = this.span;
        const live = mode === "live";

        const endTime = addMilliseconds(time, span / 4);

        const startTime = subMilliseconds(time, (span * 3) / 4);
        const queryBuffer = 0.25;
        const queryStartTime = startOfMinute(
            subMilliseconds(startTime, span * queryBuffer),
        );
        const queryEndTime = addMinutes(
            startOfMinute(addMilliseconds(endTime, span * queryBuffer)),
            1,
        );

        let aggregatesSpan: IAggregatesSpan | undefined;
        if (!showOnlyCurrentValues) {
            aggregatesSpan = deviceConfigurationStore.calculateAggregatesSpan(
                queryStartTime,
                queryEndTime,
                span,
                streamName,
                device?.id,
            );
        }
        const triggeredEvent =
            activeEvent && activeEvent.type === "datapoint-event"
                ? activeEvent
                : undefined;
        const eventTrigger =
            triggeredEvent?.eventTriggerId &&
            eventTriggerStore.get(triggeredEvent.eventTriggerId);
        const threshold =
            eventTrigger && eventTrigger?.condition?.type === "threshold"
                ? eventTrigger.condition.value
                : undefined;

        const defaultStreamThrottleHz =
            organizationStore.defaultStreamThrottleHz;

        if (aggregatesSpan) {
            const { aggStart, aggEnd, aggregateLevel } = aggregatesSpan;
            const queryAggregateData = queryStore.moduleQueryAggregates(
                filter,
                streamName,
                "numeric",
                aggStart,
                aggEnd,
                aggregateLevel,
            );

            const loading = queryAggregateData === undefined;
            const tooMuchData = queryAggregateData === "too much data";

            this.aggregateData =
                (queryAggregateData !== "too much data" &&
                    queryAggregateData) ||
                this.aggregateData;
            const aggregateData = this.aggregateData;
            const empty = aggregateData.length === 0;
            const {
                filterOptions,
                splitByOptions,
                filteredData: filteredAggregatedData,
                selectedStreams,
                splitByKey,
            } = this.filterData(aggregateData);

            return (
                <Module
                    {...this.props}
                    loading={loading}
                    empty={empty}
                    tooMuchData={tooMuchData}
                    type="numeric"
                    className={styles["numeric-module"]}
                    onResetTimeSpan={this.onResetTimeSpan}
                >
                    <Graph
                        defaultStreamThrottleHz={defaultStreamThrottleHz}
                        time={time}
                        live={live}
                        startTime={queryStartTime}
                        endTime={queryEndTime}
                        data={filteredAggregatedData}
                        threshold={threshold}
                        onSelectTime={onSelectTime}
                        splitByKey={splitByKey}
                        className={styles.graph}
                    />
                    {!configurationDisabled && (
                        <div className={styles.configuration}>
                            <SpanSelector
                                value={span}
                                onChange={this.onChangeSpan}
                            />
                            <SplitBySelector
                                value={this.splitByKey}
                                keys={splitByOptions}
                                onChange={this.onChangeSplitByKey}
                            />
                            <FilterSelector
                                value={selectedStreams}
                                keys={filterOptions}
                                onChange={this.onChangeFilter}
                            />
                        </div>
                    )}
                </Module>
            );
        } else {
            const queryData = queryStore.moduleQuery(
                filter,
                streamName,
                "numeric",
                queryStartTime,
                queryEndTime,
            );
            const loading = queryData === undefined;
            const tooMuchData = queryData === "too much data";
            const hasData = queryData !== "too much data" && queryData;
            this.data = hasData || this.data;
            const data = this.data;
            const empty = data.length === 0;

            const {
                filterOptions,
                splitByOptions,
                filteredData,
                selectedStreams,
                splitByKey,
            } = this.filterData(data);

            return (
                <Module
                    {...this.props}
                    loading={loading}
                    empty={empty}
                    tooMuchData={tooMuchData}
                    type="numeric"
                    className={styles["numeric-module"]}
                >
                    {showOnlyCurrentValues ? (
                        <>
                            {hasData && (
                                <SimpleNumericModule
                                    streamData={data}
                                    time={time}
                                />
                            )}
                        </>
                    ) : (
                        <Graph
                            defaultStreamThrottleHz={defaultStreamThrottleHz}
                            time={time}
                            live={live}
                            startTime={queryStartTime}
                            endTime={queryEndTime}
                            data={filteredData}
                            threshold={threshold}
                            onSelectTime={onSelectTime}
                            splitByKey={splitByKey}
                            className={styles.graph}
                        />
                    )}
                    {!configurationDisabled && !showOnlyCurrentValues && (
                        <div className={styles.configuration}>
                            <SpanSelector
                                value={this.span}
                                onChange={this.onChangeSpan}
                            />
                            <SplitBySelector
                                value={splitByKey}
                                keys={splitByOptions}
                                onChange={this.onChangeSplitByKey}
                            />
                            <FilterSelector
                                value={selectedStreams}
                                keys={filterOptions}
                                onChange={this.onChangeFilter}
                            />
                        </div>
                    )}
                </Module>
            );
        }
    }

    private filterData(
        data: IStreamData<"numeric">[] | IStreamAggregateData<"numeric">[],
    ) {
        let { selectedStreams, splitByKey } = this;
        const splitByOptions = multiValuedTagKeys(data);
        if (!splitByKey) {
            splitByKey = splitByOptions[0] || "";
        }
        const optionsIndex = splitByOptions.indexOf(splitByKey);

        const filterOptions = splitByOptions[optionsIndex]
            ? [
                  ...new Set(
                      data.map(_ => _.tags[splitByOptions[optionsIndex]] || ""),
                  ),
              ].sort((a, b) => a.localeCompare(b))
            : [];
        if (selectedStreams.length === 0 && filterOptions.length > 0) {
            selectedStreams = filterOptions;
        }

        // filter using lodash, as typescript doesn't like the filter function with multiple types
        // https://github.com/microsoft/TypeScript/issues/44373
        const filteredData = (
            splitByOptions.length > 0
                ? lodashFilter(
                      data,
                      (
                          d:
                              | IStreamData<"numeric">
                              | IStreamAggregateData<"numeric">,
                      ) => {
                          return selectedStreams.includes(
                              d.tags[splitByOptions[0]],
                          );
                      },
                  )
                : data
        ) as IStreamData<"numeric">[] | IStreamAggregateData<"numeric">[];
        return {
            filteredData,
            filterOptions,
            splitByOptions,
            selectedStreams,
            splitByKey,
        };
    }

    private onResetTimeSpan = () => {
        this.span = 1 * duration.minute;
        this.changeConfiguration({ span: this.span });
    };

    private changeConfiguration(updates: Partial<INumericViewConfiguration>) {
        const { streamName, onChangeConfiguration } = this.props;

        // save to local storage
        this.useModuleMemory.saveState(streamName, updates);

        if (!onChangeConfiguration) {
            return;
        }
        onChangeConfiguration({
            streamName,
            type: "numeric",
            updates,
        });
    }

    private onChangeSpan = (span: number) => {
        this.span = span;
        this.changeConfiguration({ span });
    };

    private onChangeSplitByKey = (splitByKey: string = "") => {
        this.splitByKey = splitByKey;
        this.changeConfiguration({ splitByKey });
    };

    private onChangeFilter = (selectedStreams: string[]) => {
        this.selectedStreams = selectedStreams;
    };
}
